import { supabase } from '../../supabase';
import { fetchBranches } from '../gym/catalogApi';
import { ensureGymId, getCachedGymId, setActiveGymId } from '../receptionist/gymContext';
import { cachedQuery } from '../cache/queryCache';
import { fetchTrainers } from '../receptionist/receptionistApi';
import { fetchAdminReportsPageRpc } from '../shared/adminReportsRpcApi';
import {
  getReceptionistBranch,
  getReceptionistBranchFilter,
  isReceptionistPanelScope,
} from '../gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';

const PAGE = 1000;

async function resolveGymId() {
  const cached = getCachedGymId();
  if (cached) {
    setActiveGymId(cached);
    return cached;
  }
  return ensureGymId();
}

/** Page through a query builder that already has filters/order. */
async function fetchAllLight(buildQuery) {
  const rows = [];
  let from = 0;
  for (;;) {
    const to = from + PAGE - 1;
    const { data, error } = await buildQuery().range(from, to);
    if (error) throw error;
    const chunk = data || [];
    rows.push(...chunk);
    if (chunk.length < PAGE) break;
    from += PAGE;
  }
  return rows;
}

function last12Months() {
  const months = [];
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(1);
    d.setHours(12, 0, 0, 0);
    d.setMonth(d.getMonth() - i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    months.push({
      key: `${year}-${month}`,
      label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    });
  }
  return months;
}

function paymentDate(payment) {
  const raw = payment.date || payment.paidAt || payment.paid_at || payment.createdAt || payment.created_at || '';
  const text = String(raw).trim();
  if (!text) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  }
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return '';
}

function paymentMonthKey(payment) {
  return paymentDate(payment).slice(0, 7);
}

function isMembershipPayment(payment) {
  return payment.source === 'subscription'
    || Boolean(payment.package && payment.package !== '—')
    || Boolean(payment.memberId);
}

function buildMemberReport(members, memberships, monthKey) {
  const active = members.filter((m) => m.status === 'active').length;
  const inactive = members.filter((m) => m.status === 'inactive').length;
  const frozen = memberships.filter((m) => m.frozen || m.status === 'inactive').length;
  const newThisMonth = members.filter((m) =>
    (m.joiningDate || m.createdAt || '').startsWith(monthKey),
  ).length;
  const churned = memberships.filter((m) =>
    m.status === 'expired' && (m.expiry || m.endDate || '').startsWith(monthKey),
  ).length;

  const rows = [
    { status: 'Active', count: active, newThisMonth, churned: 0 },
    { status: 'Inactive', count: inactive, newThisMonth: 0, churned },
    { status: 'Frozen', count: frozen, newThisMonth: 0, churned: 0 },
  ];

  return rows.filter((row) => row.count > 0 || row.newThisMonth > 0 || row.churned > 0);
}

function buildAttendanceReport(branches, members, attendance) {
  const memberBranch = new Map(members.map((m) => [m.id, m.branch || 'Unassigned']));
  const branchNames = branches.length
    ? branches.map((b) => b.name)
    : [...new Set(members.map((m) => m.branch).filter(Boolean))];

  const names = branchNames.length ? branchNames : ['All Branches'];
  const checkInsByBranch = {};
  const membersByBranch = {};

  names.forEach((name) => {
    checkInsByBranch[name] = 0;
    membersByBranch[name] = 0;
  });

  members.forEach((member) => {
    const branch = member.branch || names[0] || 'Unassigned';
    if (!membersByBranch[branch]) membersByBranch[branch] = 0;
    membersByBranch[branch] += 1;
  });

  attendance.forEach((log) => {
    if (log.personType && log.personType !== 'member') return;
    const branch = memberBranch.get(log.memberId) || names[0] || 'Unassigned';
    if (!checkInsByBranch[branch]) checkInsByBranch[branch] = 0;
    checkInsByBranch[branch] += 1;
  });

  const daysInPeriod = 30;

  return names.map((branch) => {
    const checkIns = checkInsByBranch[branch] || 0;
    const memberCount = membersByBranch[branch] || 0;
    const avgDaily = Math.round(checkIns / daysInPeriod);
    const percentage = memberCount
      ? Math.min(100, Math.round((checkIns / (memberCount * daysInPeriod)) * 100))
      : 0;

    return { branch, checkIns, avgDaily, percentage, memberCount };
  }).filter((row) => row.checkIns > 0 || row.memberCount > 0);
}

function buildRevenueReport(payments, months) {
  return months.map(({ key, label }) => {
    const monthPayments = payments.filter((p) => paymentMonthKey(p) === key);
    const memberships = monthPayments
      .filter(isMembershipPayment)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const other = monthPayments
      .filter((p) => !isMembershipPayment(p))
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    return {
      month: label,
      memberships,
      other,
      total: memberships + other,
    };
  }).filter((row) => row.total > 0);
}

function buildMembershipReport(memberships, payments) {
  const packages = [...new Set(memberships.map((m) => m.package).filter(Boolean))];
  if (!packages.length) return [];

  return packages.map((packageName) => {
    const packageMemberships = memberships.filter((m) => m.package === packageName);
    const active = packageMemberships.filter((m) => m.status === 'active' && !m.frozen).length;
    const renewals = packageMemberships.filter((m) => m.status === 'active').length;
    const revenue = payments
      .filter((p) => p.package === packageName)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    return { package: packageName, active, renewals, revenue };
  });
}

function buildTrainerReport(trainers, members, attendance) {
  return trainers.map((trainer) => {
    const assigned = members.filter((m) =>
      (m.trainer || '').toLowerCase() === trainer.name.toLowerCase(),
    ).length;
    const sessions = attendance.filter((log) => log.trainerId === trainer.id).length;

    return {
      trainer: trainer.name,
      members: assigned,
      sessions,
      rating: assigned > 0 ? '—' : '—',
    };
  });
}

function buildProfitReport(payments, trainers, months) {
  const monthlySalaries = trainers
    .filter((t) => t.status === 'active')
    .reduce((sum, t) => sum + (Number(t.salary) || 0), 0);

  return months.map(({ key, label }) => {
    const revenue = payments
      .filter((p) => paymentMonthKey(p) === key)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const expenses = 0;
    const salaries = monthlySalaries;
    const profit = revenue - expenses - salaries;

    return { month: label, revenue, expenses, salaries, profit };
  }).filter((row) => row.revenue > 0 || row.salaries > 0);
}

function buildBranchReport(branches, members, payments, attendance, trainers) {
  const branchNames = branches.length
    ? branches.map((b) => b.name)
    : [...new Set(members.map((m) => m.branch).filter(Boolean))];

  if (!branchNames.length) return [];

  return branchNames.map((branch) => {
    const branchMembers = members.filter((m) => m.branch === branch);
    const memberIds = new Set(branchMembers.map((m) => m.id));
    const branchPayments = payments.filter((p) =>
      memberIds.has(p.memberId) || branchMembers.some((m) => m.name === p.member),
    );
    const revenue = branchPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const branchAttendance = attendance.filter((log) =>
      memberIds.has(log.memberId),
    ).length;
    const daysInPeriod = 30;
    const attendancePct = branchMembers.length
      ? Math.min(100, Math.round((branchAttendance / (branchMembers.length * daysInPeriod)) * 100))
      : 0;
    const branchTrainers = trainers.filter((t) => t.branch === branch && t.status === 'active').length;

    return {
      branch,
      members: branchMembers.length,
      revenue,
      attendance: attendancePct,
      trainers: branchTrainers,
    };
  });
}

/** Light members — no serial/membership enrichment. */
async function loadMembersLight(gid) {
  const rows = await fetchAllLight(() => (
    supabase
      .from('members')
      .select('id, full_name, status, branch, trainer, joining_date, created_at')
      .eq('gym_id', gid)
      .order('id', { ascending: true })
  ));
  return rows.map((row) => ({
    id: row.id,
    name: row.full_name || '',
    status: row.status || 'active',
    branch: row.branch || '',
    trainer: row.trainer || '',
    joiningDate: row.joining_date || '',
    createdAt: row.created_at ? String(row.created_at).slice(0, 10) : '',
  }));
}

/** Light memberships — skip payment fee joins. */
async function loadMembershipsLight(gid) {
  const rows = await fetchAllLight(() => (
    supabase
      .from('memberships')
      .select('id, member_id, package_name, status, frozen, expiry_date')
      .eq('gym_id', gid)
      .order('id', { ascending: true })
  ));
  return rows.map((row) => ({
    id: row.id,
    memberId: row.member_id,
    package: row.package_name || '',
    status: row.status || 'active',
    frozen: Boolean(row.frozen),
    expiry: row.expiry_date || '',
    endDate: row.expiry_date || '',
  }));
}

/** Light payments — amounts/dates only. */
async function loadPaymentsLight(gid) {
  const since = new Date();
  since.setMonth(since.getMonth() - 13);
  const sinceKey = since.toISOString().slice(0, 10);

  let rows = [];
  try {
    rows = await fetchAllLight(() => (
      supabase
        .from('payments')
        .select('id, member_id, member_name, package_name, amount, paid_at, source')
        .eq('gym_id', gid)
        .gte('paid_at', sinceKey)
        .order('paid_at', { ascending: false })
        .order('id', { ascending: true })
    ));
  } catch (error) {
    // Fallback without date filter if column/index issues
    if (/paid_at|column|schema/i.test(error.message || '')) {
      rows = await fetchAllLight(() => (
        supabase
          .from('payments')
          .select('id, member_id, member_name, package_name, amount, paid_at, source')
          .eq('gym_id', gid)
          .order('id', { ascending: true })
      ));
    } else {
      throw error;
    }
  }

  return rows.map((row) => ({
    id: row.id,
    memberId: row.member_id,
    member: row.member_name || '',
    package: row.package_name || '',
    amount: Number(row.amount) || 0,
    date: row.paid_at || '',
    source: row.source || '',
  }));
}

/** Light attendance — last ~30 days for check-in stats. */
async function loadAttendanceLight(gid) {
  const since = new Date();
  since.setDate(since.getDate() - 31);
  const sinceKey = since.toISOString().slice(0, 10);

  let rows = [];
  try {
    rows = await fetchAllLight(() => (
      supabase
        .from('attendance_logs')
        .select('id, member_id, trainer_id, person_type, attendance_date')
        .eq('gym_id', gid)
        .gte('attendance_date', sinceKey)
        .order('id', { ascending: true })
    ));
  } catch (error) {
    if (/attendance_date|column|schema/i.test(error.message || '')) {
      rows = await fetchAllLight(() => (
        supabase
          .from('attendance_logs')
          .select('id, member_id, trainer_id, person_type')
          .eq('gym_id', gid)
          .order('id', { ascending: true })
      ));
    } else {
      throw error;
    }
  }

  return rows.map((row) => ({
    id: row.id,
    memberId: row.member_id,
    trainerId: row.trainer_id,
    personType: row.person_type || 'member',
    date: row.attendance_date || '',
  }));
}

function inDateWindow(day, from, to) {
  if (!from && !to) return true;
  const key = String(day || '').slice(0, 10);
  if (!key) return false;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
}

/**
 * Admin Reports — RPC snapshot first; REST lights + builders as fallback.
 */
export async function fetchAdminReports({
  dateFrom = '',
  dateTo = '',
  branch = '',
} = {}) {
  const gid = await resolveGymId();
  if (!gid) {
    throw new Error('No gym is linked to this account. Sign out and sign in again.');
  }

  const receptionist = isReceptionistPanelScope();
  const today = pkTodayKey();
  const from = receptionist ? today : String(dateFrom || '').slice(0, 10);
  const to = receptionist ? today : String(dateTo || '').slice(0, 10);
  const branchFilter = String(
    branch
    || (receptionist ? (getReceptionistBranchFilter() || getReceptionistBranch() || '') : '')
    || '',
  ).trim();
  const cacheKey = `gym:${gid}:admin-reports:v3:${from || 'all'}:${to || 'all'}:${branchFilter || 'all'}`;

  return cachedQuery(cacheKey, async () => {
    try {
      const rpc = await fetchAdminReportsPageRpc({
        gymId: gid,
        dateFrom: from,
        dateTo: to,
        branch: branchFilter,
        receptionistScope: receptionist,
      });
      if (rpc) return rpc;
    } catch (err) {
      console.warn('Admin reports RPC failed, using fallback', err?.message || err);
    }

    const monthKey = today.slice(0, 7);
    const months = last12Months();

    const [branches, members, memberships, attendance, payments, trainers] = await Promise.all([
      fetchBranches().catch(() => []),
      loadMembersLight(gid).catch(() => []),
      loadMembershipsLight(gid).catch(() => []),
      loadAttendanceLight(gid).catch(() => []),
      loadPaymentsLight(gid).catch(() => []),
      fetchTrainers().catch(() => []),
    ]);

    const windowPayments = (from || to)
      ? payments.filter((p) => inDateWindow(p.date, from, to))
      : payments;
    const windowAttendance = (from || to)
      ? attendance.filter((log) => inDateWindow(log.date, from, to))
      : attendance;

    return {
      memberReport: buildMemberReport(members, memberships, monthKey),
      attendanceReport: buildAttendanceReport(branches, members, windowAttendance),
      revenueReport: buildRevenueReport(windowPayments, months),
      membershipReport: buildMembershipReport(memberships, windowPayments),
      trainerReport: buildTrainerReport(trainers, members, windowAttendance),
      profitReport: buildProfitReport(windowPayments, trainers, months),
      branchReport: buildBranchReport(branches, members, windowPayments, windowAttendance, trainers),
      memberOptions: [],
      fromRpc: false,
    };
  }, 60_000);
}
